import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Wrench, ArrowLeft, AlertCircle } from 'lucide-react';
import { useClinicalOnboarding, OnboardingStorage } from '@/core/application/profile/useClinicalOnboarding';
import { ProfessionalProfileService } from '@/core/application/directory/ProfessionalProfileService';
import { useProfileStore } from '@/core/application/state/useProfileStore';
import { useRoleStore } from '@/core/application/state/useRoleStore';
import { WaitlistService } from '@/core/application/directory/WaitlistService';

export function BecomeProfessionalScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { magicCodeInMemory, storeMagicCode, submitAtomicOnboarding, isSubmitting, error } = useClinicalOnboarding();
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [isSuccess, setIsSuccess] = useState(false);

  // Waitlist states
  const profile = useProfileStore((state) => state.profile);
  const [hasSubmittedWaitlist, setHasSubmittedWaitlist] = useState<boolean | null>(null);
  
  // Waitlist form fields
  const [wlNombre, setWlNombre] = useState(profile?.perfil_nombre || '');
  const [wlEmail, setWlEmail] = useState(''); // El email normalmente está en auth.users, pero podemos pedirlo o tomarlo si estuviese.
  const [wlEspecialidad, setWlEspecialidad] = useState('');
  const [wlCedula, setWlCedula] = useState(profile?.perfil_dni || '');
  const [wlColegioEstado, setWlColegioEstado] = useState('');
  const [wlMpps, setWlMpps] = useState('');
  const [isSubmittingWaitlist, setIsSubmittingWaitlist] = useState(false);
  const [waitlistError, setWaitlistError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user already submitted waitlist
    if (profile?.id && !magicCodeInMemory) {
      WaitlistService.checkHasSubmittedWaitlist(profile.id).then(setHasSubmittedWaitlist);
    }
  }, [profile?.id, magicCodeInMemory]);

  const handleGoBack = async () => {
    await OnboardingStorage.clearPendingInvite();
    if (globalThis.history.length > 2) navigate(-1);
    else navigate('/hub');
  };

  useEffect(() => {
    // Capturar código vía React Router search params (reemplaza route.params)
    const capturedCode = searchParams.get('code');
    if (capturedCode) {
      storeMagicCode(capturedCode.toUpperCase());
    }
  }, [searchParams]);

  const [titulo, setTitulo] = useState('');
  const [licenciaMpps, setLicenciaMpps] = useState('');
  const [licenciaCmv, setLicenciaCmv] = useState('');
  const [licenciaFmv, setLicenciaFmv] = useState('');
  const [licenciaFpv, setLicenciaFpv] = useState('');
  const [titulosDisponibles, setTitulosDisponibles] = useState<string[]>([]);

  useEffect(() => {
    ProfessionalProfileService.getClinicalRoles().then(setTitulosDisponibles);
  }, []);

  const isTitleValid = useMemo(() => {
    return !!titulo && titulo !== 'PROFESIONAL_CLINICO' && titulosDisponibles.includes(titulo);
  }, [titulo, titulosDisponibles]);

  const handleTitleChange = (newTitle: string) => {
    setTitulo(newTitle);
    setLicenciaMpps('');
    setLicenciaCmv('');
    setLicenciaFmv('');
    setLicenciaFpv('');
    setValidationErrors({});
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    if (!titulo) {
      errors.titulo = 'Por favor selecciona tu especialidad o título principal para continuar.';
      return errors;
    }
    if (!isTitleValid) {
      errors.titulo = 'El título seleccionado no es válido.';
      return errors;
    }
    const t = titulo.toUpperCase();
    if (t === 'VETERINARIO' && !licenciaCmv) {
      errors.licenciaCmv = 'El número de Colegio de Médicos Veterinarios (CMV) es obligatorio.';
    } else if (t === 'FONOAUDIOLOGO' && !licenciaMpps) {
      errors.licenciaMpps = 'El número de registro MPPS o Gaceta Oficial es obligatorio.';
    } else if (t === 'PSICOLOGO' && !licenciaFpv) {
      errors.licenciaFpv = 'El número de Federación de Psicólogos (FPV) es obligatorio.';
    } else if (!['VETERINARIO', 'FONOAUDIOLOGO', 'PSICOLOGO'].includes(t) && !licenciaMpps) {
      errors.licenciaMpps = 'El número de registro MPPS es obligatorio.';
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});

    const payload = {
      rol_solicitado: titulo,
      titulo_principal: titulo,
      resumen_profesional: '',
      atiende_virtual: true,
      atiende_presencial: true,
      atiende_domicilio: false,
      especialidades_ids: [],
      credencial_mpps: licenciaMpps || undefined,
      credencial_cmv: licenciaCmv || undefined,
      credencial_fmv: licenciaFmv || undefined,
      credencial_fpv: licenciaFpv || undefined,
    };

    const success = await submitAtomicOnboarding(payload);
    if (success) {
      await useProfileStore.getState().fetchProfile(true);
      useRoleStore.getState().setActiveRole('PROFESSIONAL');
      setIsSuccess(true);
    }
  };

  if (isSuccess) {
    return (
      <div style={{ flex: 1, backgroundColor: '#F0F9FF', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px', minHeight: '100vh' }}>
        <div style={{ backgroundColor: '#FFFFFF', borderRadius: '24px', padding: '40px', width: '100%', maxWidth: '480px', display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: '0px 10px 30px rgba(14, 165, 233, 0.1)', border: '1px solid #E0F2FE' }}>
          <div style={{ backgroundColor: '#D1FAE5', padding: '24px', borderRadius: '60px', marginBottom: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <ShieldCheck size={80} color="#059669" />
          </div>
          <h1 style={{ fontSize: '32px', fontWeight: 900, color: '#0F172A', textAlign: 'center', marginBottom: '16px', letterSpacing: '-0.5px', margin: '0 0 16px 0' }}>
            ¡Bienvenido a la Red Profesional!
          </h1>
          <p style={{ fontSize: '16px', color: '#475569', textAlign: 'center', lineHeight: '26px', marginBottom: '40px', margin: '0 0 40px 0' }}>
            Tu perfil clínico ha sido verificado y creado de forma segura. Ya cuentas con acceso preferencial al directorio de consultantes.
          </p>
          <button
            style={{ backgroundColor: '#0F172A', width: '100%', padding: '18px', borderRadius: '16px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: 'none', cursor: 'pointer', boxShadow: '0px 4px 8px rgba(0,0,0,0.15)' }}
            onClick={() => navigate('/hub', { replace: true })}
          >
            <span style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: 700, letterSpacing: '0.5px' }}>Comenzar a explorar</span>
          </button>
        </div>
      </div>
    );
  }

  const renderOnboardingForm = () => (
    <div>
      <div style={styles.successBox}>
        <ShieldCheck size={24} color="#166534" style={{ marginRight: '8px' }} />
        <span style={styles.successText}>Acceso Exclusivo Desbloqueado</span>
      </div>

      <h3 style={styles.label}>Valida tu licencia para el directorio</h3>

      <div style={styles.field}>
        <label htmlFor="tituloPrincipal" style={styles.subLabel}>Tu Título Principal</label>
        <select
          id="tituloPrincipal"
          style={{ ...styles.selectInput, ...(validationErrors.titulo ? styles.inputError : {}) }}
          value={titulo}
          onChange={(e) => handleTitleChange(e.target.value)}
        >
          <option value="">Selecciona tu título...</option>
          {titulosDisponibles.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        {!!validationErrors.titulo && <span style={styles.fieldErrorText}>{validationErrors.titulo}</span>}
      </div>

      {isTitleValid && titulo.toUpperCase() === 'VETERINARIO' && (
        <div style={styles.field}>
          <label htmlFor="licenciaCmv" style={styles.subLabel}>Colegio de Médicos Veterinarios / CMV (Obligatorio)</label>
          <input
            id="licenciaCmv"
            style={{ ...styles.textInput, ...(validationErrors.licenciaCmv ? styles.inputError : {}) }}
            value={licenciaCmv}
            onChange={(e) => { setLicenciaCmv(e.target.value.replace(/\D/g, '')); if (validationErrors.licenciaCmv) setValidationErrors(prev => ({ ...prev, licenciaCmv: '' })); }}
            placeholder="Ej. 12345"
          />
          {!!validationErrors.licenciaCmv && <span style={styles.fieldErrorText}>{validationErrors.licenciaCmv}</span>}
        </div>
      )}

      {isTitleValid && titulo.toUpperCase() === 'FONOAUDIOLOGO' && (
        <div style={styles.field}>
          <label htmlFor="licenciaMppsFono" style={styles.subLabel}>Licencia MPPS o G.O. (Obligatoria)</label>
          <input
            id="licenciaMppsFono"
            style={{ ...styles.textInput, ...(validationErrors.licenciaMpps ? styles.inputError : {}) }}
            value={licenciaMpps}
            onChange={(e) => { setLicenciaMpps(e.target.value.toUpperCase().replace(/[^0-9GO]/g, '')); if (validationErrors.licenciaMpps) setValidationErrors(prev => ({ ...prev, licenciaMpps: '' })); }}
            placeholder="Ej. 12345 o GO123"
          />
          {!!validationErrors.licenciaMpps && <span style={styles.fieldErrorText}>{validationErrors.licenciaMpps}</span>}
        </div>
      )}

      {isTitleValid && !['VETERINARIO', 'FONOAUDIOLOGO', 'PSICOLOGO'].includes(titulo.toUpperCase()) && (
        <div style={styles.field}>
          <label htmlFor="licenciaMppsGral" style={styles.subLabel}>Licencia MPPS (Obligatoria)</label>
          <input
            id="licenciaMppsGral"
            style={{ ...styles.textInput, ...(validationErrors.licenciaMpps ? styles.inputError : {}) }}
            value={licenciaMpps}
            onChange={(e) => { setLicenciaMpps(e.target.value.toUpperCase().replace(/[^0-9GO]/g, '')); if (validationErrors.licenciaMpps) setValidationErrors(prev => ({ ...prev, licenciaMpps: '' })); }}
            placeholder="Ej. 12345 o GO123"
          />
          {!!validationErrors.licenciaMpps && <span style={styles.fieldErrorText}>{validationErrors.licenciaMpps}</span>}
        </div>
      )}

      {isTitleValid && titulo.toUpperCase() === 'PSICOLOGO' && (
        <div style={styles.field}>
          <label htmlFor="licenciaMppsPsico" style={styles.subLabel}>Licencia MPPS (Opcional)</label>
          <input
            id="licenciaMppsPsico"
            style={styles.textInput}
            value={licenciaMpps}
            onChange={(e) => setLicenciaMpps(e.target.value.toUpperCase().replace(/[^0-9GO]/g, ''))}
            placeholder="Ej. 12345 o GO123"
          />
          <label htmlFor="licenciaFpv" style={{ ...styles.subLabel, marginTop: '12px' }}>Federación de Psicólogos / FPV (Obligatoria)</label>
          <input
            id="licenciaFpv"
            style={{ ...styles.textInput, ...(validationErrors.licenciaFpv ? styles.inputError : {}) }}
            value={licenciaFpv}
            onChange={(e) => { setLicenciaFpv(e.target.value.replace(/\D/g, '')); if (validationErrors.licenciaFpv) setValidationErrors(prev => ({ ...prev, licenciaFpv: '' })); }}
            placeholder="Ej. 12345"
          />
          {!!validationErrors.licenciaFpv && <span style={styles.fieldErrorText}>{validationErrors.licenciaFpv}</span>}
        </div>
      )}

      {isTitleValid && titulo.toUpperCase() === 'MEDICO' && (
        <div style={styles.field}>
          <label htmlFor="licenciaFmv" style={styles.subLabel}>Colegio Médico / FMV (Opcional)</label>
          <input
            id="licenciaFmv"
            style={styles.textInput}
            value={licenciaFmv}
            onChange={(e) => setLicenciaFmv(e.target.value.replace(/\D/g, ''))}
            placeholder="Ej. 12345"
          />
        </div>
      )}

      {Boolean(error) && (
        <div style={styles.errorContainer}>
          <AlertCircle size={16} color="#EF4444" style={{ marginRight: '6px', flexShrink: 0 }} />
          <span style={styles.errorText}>{error}</span>
        </div>
      )}

      <button
        style={{ ...styles.button, ...(isSubmitting ? styles.buttonDisabled : {}) }}
        onClick={handleSubmit}
        disabled={isSubmitting}
      >
        {isSubmitting ? <div style={styles.spinnerWhite} /> : <span style={styles.buttonText}>Comenzar mi experiencia</span>}
      </button>
    </div>
  );

  const renderWaitlistSuccess = () => (
    <div style={styles.kycBox}>
      <ShieldCheck size={40} color="#059669" style={{ marginBottom: '16px' }} />
      <h2 style={{ ...styles.kycTitle, color: '#065F46' }}>Estás en la Lista de Espera VIP</h2>
      <p style={{ ...styles.kycText, marginBottom: '24px' }}>
        Tu solicitud ya fue registrada exitosamente. Nuestro equipo clínico está validando tus credenciales de forma rigurosa.
      </p>
      <div style={{ backgroundColor: '#D1FAE5', padding: '16px', borderRadius: '8px', width: '100%', textAlign: 'center' }}>
        <span style={{ color: '#047857', fontWeight: 600, fontSize: '14px' }}>
          Recibirás una notificación en tu correo electrónico en cuanto tu cuenta profesional sea aprobada.
        </span>
      </div>
      <div style={styles.divider} />
      <p style={styles.kycSubtext}>
        Actualmente, el acceso a la comunidad es muy limitado y por estricto orden de validación para garantizar la máxima seguridad a los pacientes.
      </p>
    </div>
  );

  const renderWaitlistForm = () => (
    <div>
      <div style={styles.kycBox}>
        <Wrench size={32} color="#0F172A" style={{ marginBottom: '16px' }} />
        <h2 style={styles.kycTitle}>Lanzamiento de Acceso Anticipado</h2>
        <p style={styles.kycText}>El registro público para especialistas no está abierto aún, pero puedes aplicar a nuestra lista VIP.</p>
        <div style={styles.divider} />
        <p style={styles.kycSubtext}>
          Completa este formulario para que el Consejo Médico evalúe tus credenciales. 
        </p>
      </div>

      <div style={{ marginTop: '24px' }}>
        <h3 style={styles.label}>Solicitud de Acceso</h3>

        <div style={styles.field}>
          <label htmlFor="wlNombre" style={styles.subLabel}>Nombre Completo</label>
          <input id="wlNombre" style={styles.textInput} value={wlNombre} onChange={(e) => setWlNombre(e.target.value)} placeholder="Ej. Dr. Juan Pérez" />
        </div>

        <div style={styles.field}>
          <label htmlFor="wlEmail" style={styles.subLabel}>Correo Electrónico Profesional</label>
          <input id="wlEmail" style={styles.textInput} value={wlEmail} onChange={(e) => setWlEmail(e.target.value)} type="email" placeholder="dr.juan@clinica.com" />
        </div>

        <div style={styles.field}>
          <label htmlFor="wlEspecialidad" style={styles.subLabel}>Disciplina</label>
          <select id="wlEspecialidad" style={styles.selectInput} value={wlEspecialidad} onChange={(e) => setWlEspecialidad(e.target.value)}>
            <option value="">Selecciona tu disciplina...</option>
            {titulosDisponibles.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div style={styles.field}>
          <label htmlFor="wlCedula" style={styles.subLabel}>Cédula de Identidad (V-/E-)</label>
          <input id="wlCedula" style={styles.textInput} value={wlCedula} onChange={(e) => setWlCedula(e.target.value.toUpperCase().replace(/[^VE0-9-]/g, ''))} placeholder="Ej. V-12345678" />
        </div>

        <div style={styles.field}>
          <label htmlFor="wlColegioEstado" style={styles.subLabel}>Colegio al que está afiliado</label>
          <input id="wlColegioEstado" style={styles.textInput} value={wlColegioEstado} onChange={(e) => setWlColegioEstado(e.target.value)} placeholder="Ej. Colegio Médico ..." />
        </div>

        <div style={styles.field}>
          <label htmlFor="wlMpps" style={styles.subLabel}>N° de Registro MPPS, FPV o CMV</label>
          <input id="wlMpps" style={styles.textInput} value={wlMpps} onChange={(e) => setWlMpps(e.target.value)} placeholder="Ej. 12345 o GO123" />
        </div>

        {Boolean(waitlistError) && (
          <div style={styles.errorContainer}>
            <AlertCircle size={16} color="#EF4444" style={{ marginRight: '6px', flexShrink: 0 }} />
            <span style={styles.errorText}>{waitlistError}</span>
          </div>
        )}

        <button
          style={{ ...styles.button, ...((isSubmittingWaitlist || !wlNombre || !wlEmail || !wlEspecialidad || !wlCedula || !wlColegioEstado || !wlMpps) ? styles.buttonDisabled : {}) }}
          disabled={isSubmittingWaitlist || !wlNombre || !wlEmail || !wlEspecialidad || !wlCedula || !wlColegioEstado || !wlMpps}
          onClick={async () => {
            if (!profile?.id) {
              setWaitlistError('Tu sesión expiró o no es válida.');
              return;
            }
            setIsSubmittingWaitlist(true);
            setWaitlistError(null);
            try {
              await WaitlistService.joinWaitlist({
                user_id: profile.id,
                nombre_completo: wlNombre,
                email: wlEmail,
                especialidad_principal: wlEspecialidad,
                cedula_identidad: wlCedula,
                colegio_estado: wlColegioEstado,
                registro_mpps: wlMpps
              });
              setHasSubmittedWaitlist(true);
              window.scrollTo(0, 0);
            } catch (err: any) {
              setWaitlistError(err.message);
            } finally {
              setIsSubmittingWaitlist(false);
            }
          }}
        >
          {isSubmittingWaitlist ? <div style={styles.spinnerWhite} /> : <span style={styles.buttonText}>Aplicar a la Lista VIP</span>}
        </button>
      </div>
    </div>
  );

  const renderMainContent = () => {
    if (magicCodeInMemory) return renderOnboardingForm();
    if (hasSubmittedWaitlist === true) return renderWaitlistSuccess();
    return renderWaitlistForm();
  };

  return (
    <div style={styles.safeArea}>
      <div style={styles.scrollContent}>
        <div style={styles.header}>
          <button onClick={handleGoBack} style={styles.backButton}>
            <ArrowLeft size={16} color="#3B82F6" style={{ marginRight: '4px' }} />
            <span style={styles.backText}>Volver</span>
          </button>
          <h1 style={styles.title}>{magicCodeInMemory ? 'Registro Profesional' : 'Alta Clínica'}</h1>
        </div>

        <div style={styles.content}>
          <p style={styles.description}>Actualmente nuestro sistema está en fase de Alpha test.</p>

          {renderMainContent()}
        </div>
      </div>
      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } } * { box-sizing: border-box; } select,input { outline: none; } select:focus, input:focus { border-color: #3B82F6 !important; }`}</style>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  safeArea: { flex: 1, backgroundColor: '#F8FAFC', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
  scrollContent: { flexGrow: 1, display: 'flex', flexDirection: 'column' },
  header: { display: 'flex', flexDirection: 'row', alignItems: 'center', padding: '20px', backgroundColor: '#FFFFFF', borderBottom: '1px solid #F1F5F9' },
  backButton: { marginRight: '16px', display: 'flex', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  backText: { fontSize: '16px', color: '#3B82F6', fontWeight: 500 },
  title: { fontSize: '20px', fontWeight: 700, color: '#0F172A', margin: 0 },
  content: { padding: '24px', maxWidth: '600px', margin: '0 auto', width: '100%', display: 'flex', flexDirection: 'column' },
  description: { fontSize: '16px', color: '#334155', lineHeight: '24px', marginBottom: '32px', margin: '0 0 32px 0' },
  label: { fontSize: '18px', fontWeight: 700, color: '#0F172A', marginBottom: '16px', display: 'block' },
  subLabel: { fontSize: '14px', fontWeight: 600, color: '#475569', marginBottom: '8px', marginTop: '16px', display: 'block' },
  selectInput: { backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '14px 16px', fontSize: '16px', color: '#0F172A', width: '100%' },
  textInput: { backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '14px 16px', fontSize: '16px', color: '#0F172A', width: '100%' },
  field: { marginBottom: '8px', display: 'flex', flexDirection: 'column' },
  successBox: { display: 'flex', flexDirection: 'row', backgroundColor: '#DCFCE7', padding: '16px', borderRadius: '8px', marginBottom: '24px', alignItems: 'center', justifyContent: 'center' },
  successText: { color: '#166534', fontWeight: 600, textAlign: 'center', fontSize: '16px' },
  errorContainer: { display: 'flex', flexDirection: 'row', alignItems: 'flex-start', marginTop: '16px', paddingLeft: '4px' },
  errorText: { color: '#EF4444', fontSize: '14px', fontWeight: 500, flex: 1, lineHeight: '20px' },
  button: { backgroundColor: '#0F172A', padding: '16px', borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '24px', border: 'none', cursor: 'pointer', width: '100%', transition: 'opacity 0.2s' },
  buttonDisabled: { opacity: 0.7, backgroundColor: '#94A3B8', cursor: 'not-allowed' },
  buttonText: { color: '#FFFFFF', fontSize: '16px', fontWeight: 600 },
  kycBox: { backgroundColor: '#F8FAFC', padding: '24px', borderRadius: '12px', border: '1px solid #E2E8F0', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  kycTitle: { fontSize: '20px', fontWeight: 700, color: '#0F172A', marginBottom: '12px', textAlign: 'center', margin: '0 0 12px 0' },
  kycText: { fontSize: '16px', color: '#334155', textAlign: 'center', lineHeight: '24px', margin: 0 },
  divider: { height: '1px', backgroundColor: '#E2E8F0', width: '100%', margin: '20px 0' },
  kycSubtext: { fontSize: '14px', color: '#64748B', textAlign: 'center', lineHeight: '22px', margin: 0 },
  inputError: { borderColor: '#EF4444' },
  fieldErrorText: { color: '#EF4444', fontSize: '13px', marginTop: '4px', fontWeight: 500, display: 'block' },
  spinnerWhite: { width: '24px', height: '24px', border: '3px solid #FFFFFF', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
